import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  AutoFixHigh as SegmentIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { SegmentationResult } from '../../../../services/abcdeAPI';

interface SegmentationResultsProps {
  segmentationResults: SegmentationResult[];
  segmentationLoading: boolean;
  showSegmentation: boolean;
  onToggleSegmentation: () => void;
  onRunSegmentation: () => void;
  onDeleteSegmentation: (segmentationId: string) => void;
}

const SegmentationResults: React.FC<SegmentationResultsProps> = ({
  segmentationResults,
  segmentationLoading,
  showSegmentation,
  onToggleSegmentation,
  onRunSegmentation,
  onDeleteSegmentation
}) => {
  const theme = useTheme();
  const [segmentationImageZoom, setSegmentationImageZoom] = useState<{[key: string]: number}>({});
  const [segmentationImagePosition, setSegmentationImagePosition] = useState<{[key: string]: {x: number, y: number}}>({});
  const [segmentationImageDragging, setSegmentationImageDragging] = useState<{[key: string]: boolean}>({});
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  const handleSegmentationImageZoomIn = (imageKey: string) => {
    setSegmentationImageZoom(prev => ({
      ...prev,
      [imageKey]: Math.min((prev[imageKey] || 1) + 0.3, 3)
    }));
  };

  const handleSegmentationImageZoomOut = (imageKey: string) => {
    setSegmentationImageZoom(prev => ({
      ...prev,
      [imageKey]: Math.max((prev[imageKey] || 1) - 0.3, 0.5)
    }));
  };

  const handleSegmentationImageResetZoom = (imageKey: string) => {
    setSegmentationImageZoom(prev => ({ ...prev, [imageKey]: 1 }));
    setSegmentationImagePosition(prev => ({ ...prev, [imageKey]: { x: 0, y: 0 } }));
  };

  const handleSegmentationImageMouseDown = useCallback((e: React.MouseEvent, imageKey: string) => {
    const currentZoom = segmentationImageZoom[imageKey] || 1;
    if (currentZoom > 1) {
      setSegmentationImageDragging(prev => ({ ...prev, [imageKey]: true }));
      const currentPos = segmentationImagePosition[imageKey] || { x: 0, y: 0 };
      e.currentTarget.setAttribute('data-drag-start-x', (e.clientX - currentPos.x).toString());
      e.currentTarget.setAttribute('data-drag-start-y', (e.clientY - currentPos.y).toString());
    }
  }, [segmentationImageZoom, segmentationImagePosition]);

  const handleSegmentationImageMouseMove = useCallback((e: React.MouseEvent, imageKey: string) => {
    if (segmentationImageDragging[imageKey]) {
      const startX = parseFloat(e.currentTarget.getAttribute('data-drag-start-x') || '0');
      const startY = parseFloat(e.currentTarget.getAttribute('data-drag-start-y') || '0');
      setSegmentationImagePosition(prev => ({
        ...prev,
        [imageKey]: {
          x: e.clientX - startX,
          y: e.clientY - startY
        }
      }));
    }
  }, [segmentationImageDragging]);

  const handleSegmentationImageMouseUp = useCallback((imageKey: string) => {
    setSegmentationImageDragging(prev => ({ ...prev, [imageKey]: false }));
  }, []);

  const handleExpandImage = (imageKey: string) => {
    setExpandedImage(expandedImage === imageKey ? null : imageKey);
  };

  if (segmentationResults.length === 0) {
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SegmentIcon color="info" />
            Technical Analysis
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={segmentationLoading ? <CircularProgress size={16} /> : <SegmentIcon />}
            onClick={onRunSegmentation}
            disabled={segmentationLoading}
            sx={{ 
              minWidth: 140,
              position: 'relative'
            }}
          >
            {segmentationLoading ? 'Analyzing...' : 'Run Segmentation'}
          </Button>
        </Box>
        
        {/* Show loading progress when analyzing */}
        {segmentationLoading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              Running technical analysis... This may take a few moments.
            </Typography>
          </Box>
        )}
        
        {!segmentationLoading && (
          <Typography variant="caption" color="text.secondary">
            No analysis results yet. Click "Run Segmentation" to start technical analysis.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SegmentIcon color="primary" />
            Technical Segmentation Analysis
            {segmentationLoading && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {segmentationLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                Updating analysis...
              </Typography>
            )}
            <IconButton
              size="small"
              color="error"
              onClick={() => segmentationResults.length > 0 && onDeleteSegmentation(segmentationResults[0]._additional.id)}
              disabled={segmentationLoading}
              title="Delete segmentation analysis"
            >
              <DeleteIcon />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              onClick={onToggleSegmentation}
              disabled={segmentationLoading}
            >
              {showSegmentation ? 'Collapse' : 'Expand'}
            </Button>
          </Box>
        </Box>
        
        {/* Show loading progress when analyzing */}
        {segmentationLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Processing segmentation analysis...
            </Typography>
          </Box>
        )}
        
        {/* Show/Hide content based on showSegmentation */}
        {showSegmentation && segmentationResults.map((result, index) => (
          <Box key={result._additional.id} sx={{ mb: 3 }}>
            {/* Analysis Images with Zoom and Expand */}
            <Typography variant="subtitle2" gutterBottom>
              Analysis Visualizations
            </Typography>
            
            {/* Expanded Image View */}
            {expandedImage && (
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {[
                  { key: 'segmentedPlot', data: result.segmentedPlot, title: 'Segmented Image' },
                  { key: 'colorplot', data: result.colorplot, title: 'Color Analysis' },
                  { key: 'assymetryPlot1', data: result.assymetryPlot1, title: 'Asymmetry Analysis 1' },
                  { key: 'assymetryplot2', data: result.assymetryplot2, title: 'Asymmetry Analysis 2' }
                ].filter(item => item.data && `${result._additional.id}_${item.key}` === expandedImage).map((item) => {
                  const imageKey = `${result._additional.id}_${item.key}`;
                  const currentZoom = segmentationImageZoom[imageKey] || 1;
                  const currentPos = segmentationImagePosition[imageKey] || { x: 0, y: 0 };
                  const isDragging = segmentationImageDragging[imageKey] || false;

                  return (
                    <Box key={item.key}>
                      {/* Expanded Image Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {item.title} - Expanded View
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <IconButton 
                            onClick={() => handleSegmentationImageZoomOut(imageKey)}
                            disabled={currentZoom <= 0.5}
                          >
                            <ZoomOutIcon />
                          </IconButton>
                          <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
                            {Math.round(currentZoom * 100)}%
                          </Typography>
                          <IconButton 
                            onClick={() => handleSegmentationImageZoomIn(imageKey)}
                            disabled={currentZoom >= 3}
                          >
                            <ZoomInIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleSegmentationImageResetZoom(imageKey)}
                          >
                            <ResetIcon />
                          </IconButton>
                          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FullscreenExitIcon />}
                            onClick={() => setExpandedImage(null)}
                          >
                            Exit Full View
                          </Button>
                        </Box>
                      </Box>

                      {/* Expanded Image Container */}
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          height: 600,
                          overflow: 'hidden',
                          position: 'relative',
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: 2,
                          cursor: currentZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                          bgcolor: alpha(theme.palette.common.black, 0.02)
                        }}
                        onMouseDown={(e) => handleSegmentationImageMouseDown(e, imageKey)}
                        onMouseMove={(e) => handleSegmentationImageMouseMove(e, imageKey)}
                        onMouseUp={() => handleSegmentationImageMouseUp(imageKey)}
                        onMouseLeave={() => handleSegmentationImageMouseUp(imageKey)}
                      >
                        <img
                          src={`${process.env.REACT_APP_API_URL}${item.data}`}
                          alt={item.title}
                          style={{ 
                            transform: `scale(${currentZoom}) translate(${currentPos.x / currentZoom}px, ${currentPos.y / currentZoom}px)`,
                            transformOrigin: 'center center',
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            userSelect: 'none',
                            pointerEvents: currentZoom > 1 ? 'auto' : 'none'
                          }}
                          draggable={false}
                          onError={(e) => {
                            console.error(`Failed to load ${item.key}:`, item.data);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                          onLoad={() => console.log(`Successfully loaded ${item.key}:`, item.data)}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Use zoom controls to examine details. Click and drag to pan when zoomed in.
                      </Typography>
                    </Box>
                  );
                })}
              </Paper>
            )}
            
            {/* Grid View - Hide when image is expanded */}
            {!expandedImage && (
              <Grid container spacing={2}>
                {[
                  { key: 'segmentedPlot', data: result.segmentedPlot, title: 'Segmented Image' },
                  { key: 'colorplot', data: result.colorplot, title: 'Color Analysis' },
                  { key: 'assymetryPlot1', data: result.assymetryPlot1, title: 'Asymmetry Analysis 1' },
                  { key: 'assymetryplot2', data: result.assymetryplot2, title: 'Asymmetry Analysis 2' }
                ].filter(item => item.data).map((item) => {
                  const imageKey = `${result._additional.id}_${item.key}`;
                  const currentZoom = segmentationImageZoom[imageKey] || 1;
                  const currentPos = segmentationImagePosition[imageKey] || { x: 0, y: 0 };
                  const isDraggingSegment = segmentationImageDragging[imageKey] || false;

                  return (
                    <Grid key={item.key} item xs={12} md={6}>
                      <Paper elevation={1} sx={{ p: 1 }}>
                        {/* Image controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleSegmentationImageZoomOut(imageKey)}
                              disabled={currentZoom <= 0.5}
                            >
                              <ZoomOutIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center', fontSize: '0.7rem' }}>
                              {Math.round(currentZoom * 100)}%
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleSegmentationImageZoomIn(imageKey)}
                              disabled={currentZoom >= 3}
                            >
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleSegmentationImageResetZoom(imageKey)}
                            >
                              <ResetIcon fontSize="small" />
                            </IconButton>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16 }} />
                            <IconButton
                              size="small"
                              onClick={() => handleExpandImage(imageKey)}
                              title="Expand to full view"
                            >
                              <FullscreenIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Image container with zoom */}
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            height: 250,
                            overflow: 'hidden',
                            position: 'relative',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            cursor: currentZoom > 1 ? (isDraggingSegment ? 'grabbing' : 'grab') : 'default',
                            bgcolor: alpha(theme.palette.common.black, 0.02)
                          }}
                          onMouseDown={(e) => handleSegmentationImageMouseDown(e, imageKey)}
                          onMouseMove={(e) => handleSegmentationImageMouseMove(e, imageKey)}
                          onMouseUp={() => handleSegmentationImageMouseUp(imageKey)}
                          onMouseLeave={() => handleSegmentationImageMouseUp(imageKey)}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL}${item.data}`}
                            alt={item.title}
                            style={{ 
                              transform: `scale(${currentZoom}) translate(${currentPos.x / currentZoom}px, ${currentPos.y / currentZoom}px)`,
                              transformOrigin: 'center center',
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'contain',
                              transition: isDraggingSegment ? 'none' : 'transform 0.1s ease-out',
                              userSelect: 'none',
                              pointerEvents: currentZoom > 1 ? 'auto' : 'none'
                            }}
                            draggable={false}
                            onError={(e) => {
                              console.error(`Failed to load ${item.key}:`, item.data);
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                            onLoad={() => console.log(`Successfully loaded ${item.key}:`, item.data)}
                          />
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                          Use controls to zoom. Click expand for full view.
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Technical Metrics */}
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <SegmentIcon color="primary" />
              Technical Metrics
            </Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Circularity:</strong> {result.circularity?.toFixed(3) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Asymmetry:</strong> {result.asymmetry?.toFixed(3) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Color Std (R):</strong> {result.std_dev_r?.toFixed(2) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Color Std (G):</strong> {result.std_dev_g?.toFixed(2) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Color Std (B):</strong> {result.std_dev_b?.toFixed(2) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Asymmetry 2:</strong> {result.asymmetry2?.toFixed(3) || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ))}
        
        {!showSegmentation && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Click "Expand" to view {segmentationResults.length} segmentation result(s)
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SegmentationResults;
